import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getPlannerMode } from '../lib/family-planner-api'
import { readSharedText } from '../lib/planner-share'
import { usePlanner } from '../components/family-planner/usePlanner'
import CalendarTab from '../components/family-planner/CalendarTab'
import SourcesTab from '../components/family-planner/SourcesTab'
import InboxTab from '../components/family-planner/InboxTab'
import TravelTab from '../components/family-planner/TravelTab'
import AssistantTab from '../components/family-planner/AssistantTab'

const TABS = [
  { id: 'calendar', label: 'Calendar', icon: '🗓️' },
  { id: 'sources', label: 'Sources', icon: '🔗' },
  { id: 'inbox', label: 'Inbox', icon: '📥' },
  { id: 'travel', label: 'Travel', icon: '🧳' },
  { id: 'assistant', label: 'Assistant', icon: '🤖' },
] as const

type TabId = (typeof TABS)[number]['id']

export default function FamilyPlannerPage() {
  // A message shared in from WhatsApp should land on the tab that reviews it.
  const [tab, setTab] = useState<TabId>(() => (readSharedText() ? 'inbox' : 'calendar'))
  const state = usePlanner()
  const pending = state.pendingCandidates.length
  const mode = getPlannerMode()

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/dashboard"
          className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">🗓️</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Family Planner</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Every calendar, email invite, and group-chat plan in one place — with getaway windows and an assistant that
            reads the schedule
          </p>
        </div>
      </div>

      {state.linkError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-6 text-red-600 dark:text-red-400 text-sm">
          {state.linkError}
        </div>
      )}
      {state.linkNotice && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 mb-6 text-emerald-600 dark:text-emerald-400 text-sm">
          {state.linkNotice}
        </div>
      )}

      {mode === 'direct' && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <span className="text-blue-600 dark:text-blue-400 mt-0.5">🔗</span>
          <div>
            <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Connected directly to your providers</p>
            <p className="text-blue-600/70 dark:text-blue-400/70 text-xs mt-0.5">
              Calendars are read straight from Google/Microsoft by this browser and refresh when you press Sync. Because
              there is no server, sync only happens while this page is open — background pulls, Gmail scanning, and
              travel pricing still need the planner service deployed.
            </p>
          </div>
        </div>
      )}

      {mode === 'local' && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <span className="text-blue-600 dark:text-blue-400 mt-0.5">💾</span>
          <div>
            <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Nothing linked yet</p>
            <p className="text-blue-600/70 dark:text-blue-400/70 text-xs mt-0.5">
              Connect a Google or Microsoft account on the <strong>Sources</strong> tab to pull in real calendars — that
              works without any backend. Until then this shows only what you enter here, saved in this browser.
            </p>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
            }`}
          >
            <span className="mr-1.5">{t.icon}</span>
            {t.label}
            {t.id === 'inbox' && pending > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-semibold">
                {pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'calendar' && <CalendarTab state={state} />}
      {tab === 'sources' && <SourcesTab state={state} />}
      {tab === 'inbox' && <InboxTab state={state} />}
      {tab === 'travel' && <TravelTab state={state} />}
      {tab === 'assistant' && <AssistantTab state={state} />}
    </div>
  )
}
